import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {filter, pairwise} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {isPlatformBrowser} from '@angular/common';
import {DcpCustomConfig} from '@app/config/DcpCustomConfig';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DcpCartService} from '@app/product/services/dcp-cart.service';
import {SessionStorageService} from '@dcp/configurator';

const REFRESH_CART_FLAG: string = 'refreshCartFlag';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class RefreshCartService {

  private isEditCartModalOpen = false;

  constructor(
    private router: Router,
    private cartService: DcpCartService,
    private sessionStorageService: SessionStorageService,
    @Inject(PLATFORM_ID) public platformId: string,
  ) {
    this.setupRefreshCartOnFirstCheckoutPage();
    this.setupReloadCartOnCheckoutReviewPage();
  }

  /**
   * Methods sets REFRESH_CART_FLAG to 'true',
   * when any checkout page is opened from non-checkout pages.
   */
  setupRefreshCartOnFirstCheckoutPage(): void {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      pairwise(),
      untilDestroyed(this)
    ).subscribe(([prev, curr]: [NavigationEnd, NavigationEnd]) => {
      if (this.checkoutRefreshCart(prev.url, curr.url)) {
        this.sessionStorageService.setItem(REFRESH_CART_FLAG, 'true');
      } else if (this.isCheckoutUrl(prev.url) && !this.isCheckoutUrl(curr.url)) {
        this.sessionStorageService.setItem(REFRESH_CART_FLAG, 'false');
      }
    });
  }

  /**
   * Refresh cart when updating product or checkout
   *
   * @param prevUrl
   * @param currentUrl
   */
  checkoutRefreshCart(prevUrl: string, currentUrl: string): boolean {
    return ((!this.isCheckoutUrl(prevUrl) && this.isCheckoutUrl(currentUrl)) || (this.isConfiguratorUrl(prevUrl) && this.isCartUrl(currentUrl)));
  }

  /**
   * Reload cart on checkout review page
   */
  setupReloadCartOnCheckoutReviewPage(): void {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      untilDestroyed(this)
    ).subscribe((e: NavigationEnd) => {
      if (this.isReviewOrderUrl(e.url)) {
        this.cartService.reloadActiveCart();
      }
    });
  }

  setEditCartModalOpen(isOpen: boolean): void {
    this.isEditCartModalOpen = isOpen;
  }

  getEditCartModalOpen(): boolean {
    return this.isEditCartModalOpen;
  }

  public hasBeenRefreshed(): void {
    this.sessionStorageService.setItem(REFRESH_CART_FLAG, 'false');
  }

  public shouldRefreshCart(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return this.sessionStorageService.getItem(REFRESH_CART_FLAG) !== 'false' || this.isEditCartModalOpen;
    }
    return false;
  }

  public refreshCart(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.sessionStorageService.setItem(REFRESH_CART_FLAG, 'true');
      this.cartService.reloadActiveCart();
      this.hasBeenRefreshed();
    }
  }

  public isCheckoutUrl(url: string): boolean {
    this.checkUrl(url);
    return url.includes(DcpCustomConfig.custom.routes.checkout);
  }

  public isCartUrl(url: string): boolean {
    this.checkUrl(url);
    return url.includes(DcpCustomConfig.custom.routes.cart);
  }

  public isConfiguratorUrl(url: string): boolean {
    this.checkUrl(url);
    return url.includes(DcpCustomConfig.custom.routes.cartEntry);
  }

  public isReviewOrderUrl(url: string): boolean {
    this.checkUrl(url);
    return url.includes(DcpCustomConfig.custom.routes.reviewOrder);
  }

  private checkUrl(url: string): void {
    if (typeof url !== 'string') {
      throw new TypeError(`Expected a string for 'url', but got ${typeof url}`);
    }
  }
}
